import { html, render } from 'lit-html';
import { appStore, actions } from '../store/appStore.js';
import { dataService } from '../services/dataService.js';
import { PackLine } from '../types/index.js';
import { router } from '../router/router.js';
import chevronDownIcon from '../icons/chevron-down.svg';
import closeIcon from '../icons/close.svg';
import './Alert.js';
import { handleSelectFocus, handleSelectChange } from '../utils/selectUtils.js';

export class PackLinesDrawer extends HTMLElement {
  private unsubscribe?: () => void;
  private expandedPackLine: string | null = null;
  private isCreatingNewPackLine: boolean = false;
  private editingHeadcount: string | null = null;

  connectedCallback() {
    this.unsubscribe = appStore.subscribe(() => {
      this.render();
    });
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  private handleOverlayClick = (event: Event) => {
    if (event.target === event.currentTarget) {
      actions.toggleDrawer();
    }
  };

  private handleCloseClick = () => {
    actions.toggleDrawer();
  };

  private togglePackLineExpansion = (packLineId: string) => {
    this.expandedPackLine = this.expandedPackLine === packLineId ? null : packLineId;
    this.render();
  };

  private handleCreatePackLineClick = () => {
    this.isCreatingNewPackLine = true;
    this.render();
  };

  private handleCancelCreate = () => {
    this.isCreatingNewPackLine = false;
    this.render();
  };

  private handleSavePackLine = async (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const state = appStore.getState();

    const newPackLineData = {
      facility_id: state.selectedFacility?.id || state.facilityId, // Get facility ID from selected facility
      cycle_id: state.currentCycle?.id || 1, // Get cycle ID from current cycle
      line_number: parseInt(formData.get('lineNumber') as string),
      owner: formData.get('lineLeadName') as string,
      employee_temp_count: parseInt(formData.get('tempWorkers') as string),
      employee_exempt_count: parseInt(formData.get('employees') as string),
    };

    try {
      const result = await dataService.createPackLine(newPackLineData);
      this.isCreatingNewPackLine = false;
      if (result?.pack_line?.id) {
        this.expandedPackLine = result.pack_line.id.toString();
      }
      this.render();
    } catch (error) {
      console.error('Failed to create pack line:', error);
      // TODO: Show error message to user
    }
  };

  private handleToggleBreak = async (event: Event, packLine: PackLine) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      if (packLine.isOnBreak) {
        // If on break, start a new session to end the break
        await dataService.managePackSession(
          packLine.id,
          'start',
          packLine.tempWorkers || 0,
          packLine.exemptWorkers || 0,
      );
        console.log(`Ended break for pack line: `, packLine.id);
      } else {
        // If not on break, end the current session to start a break
        // Send current worker count to preserve it during the break
        await dataService.managePackSession(packLine.id, 'break');
        console.log(`Started break for pack line: `, packLine.id);
      }
    } catch (error) {
      console.error('Failed to toggle break:', error);
      // TODO: Show error message to user
    }
  };

  private handleEndPackSession = async (event: Event, packLine: PackLine) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      // Call the API to end the pack session
      await dataService.managePackSession(packLine.id, 'end');

      // Check if we're currently on the pack line details page for this pack line
      const currentPath = window.location.pathname;
      const isOnPackLineDetailsPage = currentPath === `/pack-line/${packLine.id}`;

      // If we're on the pack line details page, navigate back to route prefix
      if (isOnPackLineDetailsPage) {
        router.navigate('/');
      }

      console.log('Ended pack session for:', packLine.id);
    } catch (error) {
      console.error('Failed to end pack session:', error);
      actions.addAlert('Failed to end pack session. Please try again.', 'error', 'drawer');
    }
  };

  private handleEditHeadcount = (event: Event, packLine: PackLine) => {
    event.preventDefault();
    event.stopPropagation();
    this.editingHeadcount = packLine.id.toString();
    this.render();
  };

  private handleCancelHeadcountEdit = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    this.editingHeadcount = null;
    this.render();
  };


  private handleSaveHeadcount = async (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);

    const packLineId = this.editingHeadcount;
    if (!packLineId) return;

    const updatedExemptWorkers = parseInt(formData.get('employees') as string);
    const updatedTempWorkers = parseInt(formData.get('tempWorkers') as string);

    try {
      // Update the employee counts on the server by calling the session management endpoint
      await dataService.managePackSession(
        parseInt(packLineId),
        'start',
        updatedTempWorkers,
        updatedExemptWorkers
      );

      this.editingHeadcount = null;
      this.render();
    } catch (error) {
      console.error('Failed to update headcount:', error);
      actions.addAlert('Failed to update headcount. Please try again.', 'error', 'drawer');
    }
  };

  private handleLinkLineClick = async (event: Event, packLine: PackLine) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      // Link device to pack line (same effect as scanning QR code)
      await dataService.linkDeviceToPackline(packLine.id);

      // Close the drawer on successful linking
      actions.toggleDrawer();
    } catch (error) {
      actions.addAlert('Failed to link to pack line. Please try again.', 'error', 'drawer');
    }
  };

  private renderLinkLineButton = (packLine: PackLine) => {
    if (packLine.isLinked) {
      return html `<div class="px-4 py-2 bg-green-100 text-green-800 border border-green-800">
        Linked Pack Line
      </div>`;
    }

    return html`
      <button
        @click=${(event: Event) => this.handleLinkLineClick(event, packLine)}
        class="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium text-sm"
      >
        Work on Line ${packLine.lineNumber}
      </button>
    `;
  };

  private renderPackLineForm = () => html`
    <div class="p-6 bg-gray-50 rounded-lg mx-4 mb-4">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Create Pack Line</h3>
      <form @submit=${this.handleSavePackLine}>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Line lead</label>
            <input
              type="text"
              name="lineLeadName"
              placeholder="Enter first and last name..."
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>

          <div class="grid grid-cols-3 gap-4 items-end">
            <div class="flex flex-col">
              <label class="block text-sm font-medium text-gray-700 mb-1">Line number</label>
              <select name="lineNumber" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" @change=${handleSelectChange} @focus=${handleSelectFocus} required>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
              </select>
            </div>

            <div class="flex flex-col">
              <label class="block text-sm font-medium text-gray-700 mb-1">Employees (including line lead)</label>
              <select name="employees" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" @change=${handleSelectChange} @focus=${handleSelectFocus} required>
                ${Array.from({length: 31}, (_, i) => html`<option value="${i}">${i}</option>`)}
              </select>
            </div>

            <div class="flex flex-col">
              <label class="block text-sm font-medium text-gray-700 mb-1">Temp workers</label>
              <select name="tempWorkers" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" @change=${handleSelectChange} @focus=${handleSelectFocus} required>
                ${Array.from({length: 31}, (_, i) => html`<option value="${i}">${i}</option>`)}
              </select>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            @click=${this.handleCancelCreate}
            class="px-4 py-2 text-emerald-600 border border-emerald-300 rounded-lg hover:bg-emerald-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  `;

  private renderHeadcountEditForm = (packLine: PackLine) => html`
    <div class="p-6 bg-gray-50 rounded-lg mx-4 mb-4">
      <div class="mb-6">
        <h3 class="text-xl font-bold text-gray-900">${packLine.lineLeadName}</h3>
        <p class="text-sm text-gray-600">Line ${packLine.lineNumber} <span class="text-green-600 font-medium">✓ Linked</span></p>
      </div>

      <form @submit=${this.handleSaveHeadcount}>
        <div class="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Employees</label>
            <select name="employees" class="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" @change=${handleSelectChange} @focus=${handleSelectFocus}>
              ${Array.from({length: 21}, (_, i) => html`
                <option value="${i}" ?selected=${packLine.exemptWorkers === i}>${i}</option>
              `)}
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Temp workers</label>
            <select name="tempWorkers" class="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" @change=${handleSelectChange} @focus=${handleSelectFocus}>
              ${Array.from({length: 21}, (_, i) => html`
                <option value="${i}" ?selected=${packLine.tempWorkers === i}>${i}</option>
              `)}
            </select>
          </div>
        </div>

        <div class="flex justify-end space-x-3">
          <button
            type="button"
            @click=${this.handleCancelHeadcountEdit}
            class="px-4 py-2 text-emerald-600 border border-emerald-300 rounded-lg hover:bg-emerald-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  `;

  private renderPackLine = (packLine: PackLine) => {
    const isExpanded = this.expandedPackLine === packLine.id.toString();

    return html`
      <div class="border-b border-gray-200 last:border-b-0">
        <div
          class="p-4 cursor-pointer hover:bg-gray-50"
          @click=${() => this.togglePackLineExpansion(packLine.id.toString())}
        >
          <div class="flex items-center justify-between">
            <div>
              <h3 class="font-semibold text-gray-900">${packLine.lineLeadName}</h3>
              <p class="text-sm text-gray-600">Line ${packLine.lineNumber}</p>
            </div>
            <div class="text-right">
              <div class="flex space-x-4 text-sm text-gray-600">
                <div>
                  <span class="font-semibold text-gray-900">${packLine.exemptWorkers}</span>
                  <div class="text-xs">Employees</div>
                </div>
                <div>
                  <span class="font-semibold text-gray-900">${packLine.tempWorkers}</span>
                  <div class="text-xs">Temp-workers</div>
                </div>
                <img src="${chevronDownIcon}" class="w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-180' : ''}" alt="Toggle" />
              </div>
            </div>
          </div>
        </div>

        ${isExpanded ? html`
          <div class="px-4 pb-4 bg-gray-50 border-t border-gray-200" @click=${(e: Event) => e.stopPropagation()}>
            <div class="flex justify-between items-start">
              <div class="flex flex-col items-center p-4">
                ${this.renderLinkLineButton(packLine)}
              </div>
              <div class="flex flex-col space-y-2 mt-6">
                <button
                  @click=${(event: Event) => this.handleToggleBreak(event, packLine)}
                  class="px-4 py-2 text-sm rounded-lg ${packLine.isOnBreak ? 'bg-black text-white hover:bg-gray-800' : 'text-emerald-600 border border-emerald-300 hover:bg-emerald-50'}"
                >
                  ${packLine.isOnBreak ? 'End Break' : 'Start a break'}
                </button>
                <button
                  @click=${(event: Event) => this.handleEndPackSession(event, packLine)}
                  class="px-4 py-2 text-emerald-600 border border-emerald-300 rounded-lg hover:bg-emerald-50 text-sm"
                >
                  End pack session
                </button>
                <button
                  @click=${(event: Event) => this.handleEditHeadcount(event, packLine)}
                  class="px-4 py-2 text-emerald-600 border border-emerald-300 rounded-lg hover:bg-emerald-50 text-sm"
                >
                  Edit headcount
                </button>
              </div>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  };

  private render() {
    const state = appStore.getState();
    const { isDrawerOpen, packLines, isLoading } = state;

    if (!isDrawerOpen) {
      render(html``, this);
      return;
    }

    // Find the pack line being edited for headcount
    const editingPackLine = this.editingHeadcount ?
      packLines.find(pl => pl.id.toString() === this.editingHeadcount) : null;

    const template = html`
      <div class="drawer-overlay" @click=${this.handleOverlayClick}>
        <div class="drawer open" @click=${(e: Event) => e.stopPropagation()}>
          <div class="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 class="text-xl font-semibold text-gray-900">PACK LINES</h2>
            <button
              @click=${this.handleCloseClick}
              class="text-gray-400 hover:text-gray-600"
            >
              <img src="${closeIcon}" class="w-6 h-6" alt="Close" />
            </button>
          </div>

          <alert-component scope="drawer"></alert-component>

          ${isLoading && packLines.length === 0 ? html`
            <div class="flex-1 flex items-center justify-center">
              <loading-spinner size="md" color="blue" text="Loading pack lines..."></loading-spinner>
            </div>
          ` : html`
            <div class="flex-1 overflow-y-auto">
            ${this.isCreatingNewPackLine ? html`
              ${this.renderPackLineForm()}
            ` : editingPackLine ? html`
              ${this.renderHeadcountEditForm(editingPackLine)}
            ` : html`
              ${packLines.length === 0 ? html`
                <div class="text-center text-gray-500 py-12">
                  No Active Pack Lines
                </div>
              ` : html`
                <div class="divide-y divide-gray-200">
                  ${packLines.map(this.renderPackLine)}
                </div>
              `}

              <div class="p-4">
                <button
                  @click=${this.handleCreatePackLineClick}
                  class="w-full bg-emerald-500 hover:bg-emerald-400 text-white py-3 rounded-md font-semibold"
                >
                  Create a Pack Line
                </button>
              </div>
            `}
            </div>
          `}
        </div>
      </div>
    `;

    render(template, this);
  }
}

customElements.define('pack-lines-drawer', PackLinesDrawer);
